import { PlayerConfigBuilder } from '@top/player-block-web';
import type { MuxConfig, Player } from '@top/player-block-web';
import type { MetadataState } from '../store/reducers/metadataReducer';
import type { AuthState } from '../store/reducers/authReducer';
import { clearLtpConsentTrackPending, shouldEnableMux } from './muxConsent';
import {
    getRawMuxEnvironmentKey,
    muxDisabledConfig,
    resolveMuxEnvironmentKey,
    syncMuxKeySessionStorageIfConsented,
} from './muxKey';
import { isMuxDebugEnabled, logPlayMuxDecision, logSetupMuxDecision, muxDebugLog } from './muxDebug';

let warnedMissingMuxKeyInDev = false;

/**
 * Whether the mux block last passed to `player.setup` (via {@link buildTopPlayerSetupConfig}) had
 * `enabled: true`. Used so consent epics do not assume Mux is on just because consent is ready now —
 * the first setup often ran before WM/WBD hydrated.
 */
let muxEnabledAtLastPlayerSetup = false;

export function recordMuxEnabledAtLastPlayerSetup(enabled: boolean): void {
    if (isMuxDebugEnabled() && muxEnabledAtLastPlayerSetup !== enabled) {
        muxDebugLog('recordMuxEnabledAtLastPlayerSetup', { now: enabled, was: muxEnabledAtLastPlayerSetup });
    }
    muxEnabledAtLastPlayerSetup = enabled;
}

export function getMuxEnabledAtLastPlayerSetup(): boolean {
    return muxEnabledAtLastPlayerSetup;
}

/** Mux block passed to `PlayerConfigBuilder.forSetup().withMux(...)` — Tub only creates Mux when this is enabled here. */
export function buildSetupMuxConfig(): MuxConfig {
    if (!shouldEnableMux()) {
        console.log('buildSetupMuxConfig: shouldEnableMux() is false');
        const disabled = muxDisabledConfig();
        logSetupMuxDecision(disabled, 'shouldEnableMux() is false');
        return disabled;
    }

    const environmentKey = resolveMuxEnvironmentKey();
    console.log('buildSetupMuxConfig: environmentKey', environmentKey);
    const environment = window.AS.Environment.getEnv();

    if (!environmentKey) {
        console.log('buildSetupMuxConfig: environmentKey is empty');
        if (
            process.env.NODE_ENV === 'development' &&
            shouldEnableMux() &&
            !getRawMuxEnvironmentKey() &&
            !warnedMissingMuxKeyInDev
        ) {
            warnedMissingMuxKeyInDev = true;
            console.warn(
                '[asvp] Consent allows Mux but no env key (set MUX_KEY at build, window.__ASVP_MUX_KEY__, sessionStorage asvp_mux_environment_key, or ASVP.setMuxEnvironmentKeyForSession()). Mux stays off.'
            );
        }
        const disabled = muxDisabledConfig();
        logSetupMuxDecision(disabled, 'no resolved env key for setup (need key + consent)');
        return disabled;
    }

    syncMuxKeySessionStorageIfConsented();

    clearLtpConsentTrackPending();

    const enabled: MuxConfig = {
        playerName: 'top_web',
        playerVersion: process.env.VERSION,
        debug: environment !== 'production',
        enabled: true,
        environmentKey,
        metadata: { subPropertyId: 'adult_swim' },
    };
    logSetupMuxDecision(enabled, 'Mux enabled for setup');
    return enabled;
}

/**
 * Mux for play / `updateConfig`.
 * Gated by `shouldEnableMux()` first, then a non-empty env key from `resolveMuxEnvironmentKey()`
 * (which also requires consent). TOP must not pass `enabled: true` with an empty key — that still
 * targets litix “inferred” and sends beacons.
 */
type PlayConfigBuilder = ReturnType<typeof PlayerConfigBuilder.forPlay>;

/**
 * Single place to attach play-time Mux for `updateConfig` — use from every playback epic
 * instead of repeating `.withMux(buildMuxConfig(...))`.
 */
export function applyMuxToPlayBuilder(
    builder: PlayConfigBuilder,
    metadata: MetadataState,
    player: Player,
    authState: AuthState | null
): PlayConfigBuilder {
    return builder.withMux(buildMuxConfig(metadata, player, authState));
}

export function buildMuxConfig(
    metadata: MetadataState,
    player: Player,
    authState: AuthState | null = null
): MuxConfig {
    if (!shouldEnableMux()) {
        const disabled = muxDisabledConfig();
        logPlayMuxDecision(disabled, 'shouldEnableMux() is false');
        return disabled;
    }

    const playerName = 'top_web';
    const environment = window.AS.Environment.getEnv();
    const environmentKey = resolveMuxEnvironmentKey();

    let videoTitle = metadata.title;
    let videoSeries = metadata.collectionTitle;

    if (metadata.videoType === 'stream') {
        if (metadata.streamTitle !== '') {
            videoTitle = metadata.streamTitle;
        }
        videoSeries = metadata.title;
    }

    if (!environmentKey) {
        const disabled = muxDisabledConfig();
        logPlayMuxDecision(disabled, 'no resolved env key for play');
        return disabled;
    }

    syncMuxKeySessionStorageIfConsented();

    clearLtpConsentTrackPending();

    const enabled: MuxConfig = {
        playerName,
        playerVersion: process.env.VERSION,
        debug: environment !== 'production',
        enabled: true,
        environmentKey,
        metadata: {
            subPropertyId: 'adult_swim',
            videoContentType: metadata.videoType,
            videoTitle,
            videoSeries,
            customData1: authState?.adobeHashId,
            customData2: authState?.isAuthenticated.toString(),
            customData6: authState?.providerDisplayName,
            customData10: (<any>window)?.WM?.PSM?.getWMUKID(),
        },
    };
    logPlayMuxDecision(enabled, 'Mux enabled for play');
    return enabled;
}
